/**
 * Storage abstraction for DEX UI.
 * Handles localStorage vs sessionStorage based on user preference.
 * Keys are always stored in localStorage (for history), but active key
 * location depends on rememberMe setting.
 */

const STORED_KEYS_KEY = 'dex_stored_keys';
const ACTIVE_KEY_KEY = 'dex_active_key';
const REMEMBER_ME_KEY = 'dex_remember_me';

export const storage = {
  /**
   * Get the appropriate storage based on rememberMe setting.
   * @param {boolean} rememberMe - Whether to use localStorage
   * @returns {Storage} localStorage or sessionStorage
   */
  getStorage(rememberMe) {
    return rememberMe ? localStorage : sessionStorage;
  },

  /**
   * Get all stored API keys (always from localStorage for persistence).
   * @returns {Array} Array of stored key objects
   */
  getKeys() {
    try {
      const keysJson = localStorage.getItem(STORED_KEYS_KEY);
      return keysJson ? JSON.parse(keysJson) : [];
    } catch (error) {
      console.error('Error reading stored keys:', error);
      return [];
    }
  },

  /**
   * Save or update a key in stored keys.
   * @param {Object} keyData - {key, role, partyId}
   */
  saveKey(keyData) {
    try {
      const keys = this.getKeys();
      const existing = keys.findIndex(k => k.key === keyData.key);

      if (existing >= 0) {
        // Update existing key
        keys[existing] = { ...keys[existing], ...keyData, lastUsed: Date.now() };
      } else {
        // Add new key
        keys.push({ ...keyData, lastUsed: Date.now() });
      }

      localStorage.setItem(STORED_KEYS_KEY, JSON.stringify(keys));
    } catch (error) {
      console.error('Error saving key:', error);
    }
  },

  /**
   * Remove a specific key from stored keys.
   * @param {string} key - The API key to remove
   */
  removeKey(key) {
    try {
      const keys = this.getKeys().filter(k => k.key !== key);
      localStorage.setItem(STORED_KEYS_KEY, JSON.stringify(keys));
    } catch (error) {
      console.error('Error removing key:', error);
    }
  },

  /**
   * Get the currently active API key.
   * @param {boolean} rememberMe - Whether to check localStorage
   * @returns {string|null} The active API key or null
   */
  getActiveKey(rememberMe) {
    // Check both storages - session takes priority if exists
    const sessionKey = sessionStorage.getItem(ACTIVE_KEY_KEY);
    if (sessionKey) return sessionKey;

    // Only check localStorage if rememberMe is true
    if (rememberMe) {
      return localStorage.getItem(ACTIVE_KEY_KEY);
    }

    return null;
  },

  /**
   * Set the currently active API key.
   * @param {string} key - The API key to set as active
   * @param {boolean} rememberMe - Whether to persist in localStorage
   */
  setActiveKey(key, rememberMe) {
    const store = this.getStorage(rememberMe);
    store.setItem(ACTIVE_KEY_KEY, key);

    // Also save rememberMe preference
    localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify(rememberMe));
  },

  /**
   * Get the saved rememberMe preference.
   * @returns {boolean} Whether rememberMe was enabled
   */
  getRememberMe() {
    try {
      const saved = localStorage.getItem(REMEMBER_ME_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  },

  /**
   * Clear all stored data (for logout).
   * Removes everything from both localStorage and sessionStorage.
   */
  clearAll() {
    localStorage.removeItem(STORED_KEYS_KEY);
    localStorage.removeItem(ACTIVE_KEY_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
    sessionStorage.removeItem(ACTIVE_KEY_KEY);
  },

  /**
   * Check if there's a restorable session.
   * @returns {Object|null} {key, rememberMe} or null
   */
  getRestorableSession() {
    const rememberMe = this.getRememberMe();
    const key = this.getActiveKey(rememberMe);

    if (key) {
      return { key, rememberMe };
    }
    return null;
  }
};
